import { Badge } from '@artic/ui/badge';
import { useFeatureFlag } from '@artic/ui/feature-flag';
import { FullHeightChrome, FullHeightStack } from '@artic/ui/full-height-layout';
import { Skeleton } from '@artic/ui/skeleton';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useQueryState } from 'nuqs';
import { useMemo } from 'react';

import { usePartnerId } from '../../features/inbox/use-partner-name';
import { partnerContainersQueryOptions } from '../../features/products/queries';
import { ProjectPageHeader } from '../../features/project-planning/project-page-header';
import { ProjectPlanningEditor } from '../../features/project-planning/project-planning-editor';
import { deriveIsProjectRedrafting } from '../../features/project-planning/project-redrafting';
import { ProjectStepper } from '../../features/project-planning/project-stepper';
import {
  projectArchivedWorkOrdersQueryOptions,
  projectPlanningDetailQueryOptions,
} from '../../features/project-planning/queries';
import { projectEditorSearchParsers } from '../../features/project-planning/search';
import { useProjectChannel } from '../../features/realtime/use-project-channel';

export const Route = createFileRoute('/projects/$projectId')({
  staticData: {
    breadcrumb: (match, queryClient) => {
      const { projectId } = match.params as { projectId: string };
      const data = queryClient.getQueryData(projectPlanningDetailQueryOptions(projectId).queryKey);
      return data?.project_name ?? null;
    },
    layout: 'full-height',
  },
  component: ProjectPlanningPage,
});

function ProjectPlanningPage() {
  const planner = useFeatureFlag('planner');

  if (!planner) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Feature Not Available</h2>
        <p className="text-muted-foreground text-sm">
          Production Planning is not enabled for your organisation.
        </p>
      </div>
    );
  }

  return <ProjectPlanningContent />;
}

function ProjectPlanningContent() {
  const { projectId } = Route.useParams();
  const partnerId = usePartnerId();
  const workloadCategoriesEnabled = useFeatureFlag('workload_categories');

  // Realtime presence + live work-order updates (gated per partner). Joining by
  // the route param keeps the channel topic in lockstep with the query cache
  // key the editor invalidates on inbound updates.
  const realtimeEnabled = useFeatureFlag('quality_realtime');
  const { viewers, channel: realtimeChannel } = useProjectChannel(projectId, {
    enabled: realtimeEnabled,
  });
  // Shared with the toolbar's "Show archived" toggle (same URL param) — when
  // on, archived work orders are fetched separately and merged in for display
  // so they can be selected and unarchived.
  const [showArchived] = useQueryState('show_archived', projectEditorSearchParsers.show_archived);

  const {
    data: project,
    isLoading: projectLoading,
    isError: projectError,
  } = useQuery(projectPlanningDetailQueryOptions(projectId));

  const { data: archivedWorkOrders = [] } = useQuery(
    projectArchivedWorkOrdersQueryOptions(projectId, showArchived),
  );

  const {
    data: containers,
    isLoading: containersLoading,
    isError: containersError,
  } = useQuery(partnerContainersQueryOptions(partnerId));

  // Merge archived rows in only when the toggle is on. Returns the base
  // `project` identity unchanged otherwise, so the editor's row memoisation
  // stays stable during normal (non-archived) editing.
  const projectWithArchived = useMemo(() => {
    if (!project) return undefined;
    if (!showArchived || archivedWorkOrders.length === 0) return project;
    return { ...project, work_orders: [...project.work_orders, ...archivedWorkOrders] };
  }, [project, showArchived, archivedWorkOrders]);

  if (projectLoading || containersLoading) {
    return (
      <div className="flex flex-col gap-2 p-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-[60vh] w-full" />
      </div>
    );
  }

  if (projectError || containersError) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Error Loading Project</h2>
        <p className="text-muted-foreground text-sm">
          Could not load the project or container configuration. Please try again.
        </p>
      </div>
    );
  }

  if (!project || !containers) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <Badge variant="outline">Project not found</Badge>
      </div>
    );
  }

  const isProjectRedrafting = deriveIsProjectRedrafting(
    project.project_status,
    project.work_orders,
  );

  return (
    <FullHeightStack>
      <FullHeightChrome>
        <ProjectStepper
          projectId={project.project_id}
          active="work_orders"
          projectStatus={project.project_status}
          isProjectRedrafting={isProjectRedrafting}
        />
      </FullHeightChrome>
      <FullHeightChrome>
        <ProjectPageHeader project={project} activeView="grid" viewers={viewers} />
      </FullHeightChrome>
      <ProjectPlanningEditor
        project={projectWithArchived ?? project}
        containers={containers}
        showWorkloadCategories={workloadCategoriesEnabled}
        realtimeChannel={realtimeChannel}
      />
    </FullHeightStack>
  );
}
